import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { ScreenManager } from "../managers/ScreenManager";
import { TextureStatesData } from "../types/types";
import { GetBounds } from "../utils/GetBounds";
import { ToggleButton } from "./ToggleButton";

/**
 * @example
 *  myButton = new IconToggleButton(..)
 *  myButton.InitBase(..)
 *  myButton.InitToggle(..)
 *  myButton?.SetProps(..)
 *  myButton.InitIcon(..)
 *  myButton.InitLabel(..)
 *  myButton.PostInit(..)
 */
export class LabelIconToggleButton 
    extends ToggleButton
{
    // icon
    public icon: Phaser.GameObjects.Image;
    protected iconOffset: Phaser.Geom.Point;
    protected iconTextureDatas: TextureStatesData;
    protected iconScale: number;

    // label
    public label: Phaser.GameObjects.Text;
    protected selectedLabel: string;
    protected deselectedLabel: string;
    protected labelOffset: Phaser.Geom.Point;
    protected deselectedTextStyle: Phaser.Types.GameObjects.Text.TextStyle;
    protected selectedTextStyle: Phaser.Types.GameObjects.Text.TextStyle;


    constructor(scene: Phaser.Scene)
    {
        super(scene);
    }

    public InitLabel(
        label: Phaser.GameObjects.Text,
        selectedLabel: string, 
        deselectedLabel: string,
        labelOffset: Phaser.Geom.Point = undefined,
        selectedTextStyle: Phaser.Types.GameObjects.Text.TextStyle = undefined,
        deselectedTextStyle: Phaser.Types.GameObjects.Text.TextStyle = undefined): LabelIconToggleButton
    {
        this.label = label;
        this.selectedLabel = selectedLabel;
        this.deselectedLabel = deselectedLabel;
        this.deselectedTextStyle = deselectedTextStyle;
        this.selectedTextStyle = selectedTextStyle;

        this.labelOffset = labelOffset != undefined ? labelOffset : new Phaser.Geom.Point();
        this.label.setPosition(labelOffset.x, labelOffset.y);
        this.add(this.label);

        return this;
    }

    /**
     * Sets an additional image, to show or hide as button is selected/deselected
     * @param image 
     */
    public InitIcon(
        iconTextureDatas: TextureStatesData,
        iconOffset: Phaser.Geom.Point = undefined,
        iconScale: number = 1
    ): LabelIconToggleButton {
        this.iconTextureDatas = iconTextureDatas;
        this.iconOffset = iconOffset != undefined ? iconOffset : new Phaser.Geom.Point();
        this.iconScale = iconScale;

        this.icon = this.scene.make.image({x:iconOffset.x, y:iconOffset.y, key:iconTextureDatas.out.texture, frame:iconTextureDatas.out.frame});
        // this.icon.setScale(iconScale * ScreenManager.svScaleFactor);
        this.add(this.icon);

        return this;
    }
    
    public override PostInit(hitAreaExtrusion: number = 0): void
    {
        // > hitAreaExtrusion
        // > listeners (buttonTextureDatas)
        // > SetInteractive
        // > UpdateHitArea
        super.PostInit(hitAreaExtrusion);
    }

    /**
     * Center icon+label nicely, first icon, then label, with 'margin' inbetween.
     */
    protected UpdatePositions(): void
    {
        // const totalWidth:number = this.label.displayWidth + this.margin + this.icon.displayWidth;
        // in our case, background is centered, so total content is as well
        // console.log(totalWidth, this.icon.displayWidth)
        this.icon.setPosition(
            // this.iconOffset.x - (totalWidth/2) + (this.icon.displayWidth/2),
            this.iconOffset.x,
            this.iconOffset.y
        );
        this.label.setPosition(
            // this.labelOffset.x + (totalWidth/2) - (this.label.displayWidth/2), 
            this.labelOffset.x, 
            this.labelOffset.y
        );
    }

    protected override UpdateHitArea(): void
    {
        super.UpdateHitArea();
        
        // const labelRect: Phaser.Geom.Rectangle = GetBounds(this.label, this.hitAreaExtrusion);
        const labelRect: Phaser.Geom.Rectangle = GetBounds(this.label, 25);
        // const iconRect: Phaser.Geom.Rectangle = GetBounds(this.icon, this.hitAreaExtrusion);
        Phaser.Geom.Rectangle.Union(this.cachedHitArea, labelRect, this.cachedHitArea);
        // Phaser.Geom.Rectangle.Union(this.cachedHitArea, iconRect, this.cachedHitArea);

        if (this.input != null) {
            this.input.hitArea = this.cachedHitArea;
            this.input.hitAreaCallback = this.cachedHitAreaCallback;
        }
    }

    public SetLabel(selectedLabel: string, deselectedLabel: string = undefined): void
    {
        if (deselectedLabel == undefined) {
            deselectedLabel = selectedLabel;
        }
        this.label.text = this.isSelected ? selectedLabel : deselectedLabel;
        this.selectedLabel = selectedLabel;
        this.deselectedLabel = deselectedLabel;

        this.UpdatePositions();
        this.UpdateHitArea();
    }

    public SetLabelOffset(value: Phaser.Geom.Point): void
    {
        this.labelOffset = value;
        
        this.UpdatePositions();
        this.UpdateHitArea();
    }

    public SetIconOffset(value: Phaser.Geom.Point): void
    {
        this.iconOffset = value;

        this.UpdatePositions();
        this.UpdateHitArea();
    }

    protected override UpdateViewFromSelected(): void
    {
        // toggle button texture view update
        super.UpdateViewFromSelected();

        // following is icon texture view update
        // if selected
        if (this.isSelected) {
            // icon
            this.icon.setTexture(this.iconTextureDatas.over.texture, this.iconTextureDatas.over.frame);
            
            // label
            this.label.text = this.selectedLabel;
            if (this.selectedTextStyle) {
                // console.log("[pre] selected; txt->metrics:",this.label.getTextMetrics());
                if (this.selectedTextStyle.metrics) {
                    // NOTE: myText.setStyle recalculates metrics [Samsung Internet fail]
                    // must set style without updating text, set metrics to text and update text manually.
                    this.label.style.setStyle(this.selectedTextStyle, false, false);
                    this.label.style["metrics"] = this.selectedTextStyle.metrics;
                    this.label.updateText();
                }
                else {
                    this.label.setStyle(this.selectedTextStyle);
                }
                // console.log("[post] selected; txt->metrics:",this.label.getTextMetrics());
            }
        }
        // if not selected
        // use either 'out' or 'disabled' texture, based on isInteractive
        else {
            // if is interactive
            if (this.isInteractive) {
                // use 'out' texture (expect exists)
                this.icon.setTexture(
                    this.iconTextureDatas.out.texture, 
                    this.iconTextureDatas.out.frame);
            }
            // if disabled
            else {
                // if got disabled texture
                if (this.iconTextureDatas.disabled != null) {
                    // use disabled texture
                    this.icon.setTexture(
                        this.iconTextureDatas.disabled.texture, 
                        this.iconTextureDatas.disabled.frame);
                }
                // if dont have disabled texture
                else {
                    // use 'out' texture instead
                    this.icon.setTexture(
                        this.iconTextureDatas.out.texture, 
                        this.iconTextureDatas.out.frame);
                }
            }

            // label
            if (this.deselectedTextStyle) {
                // console.log("[pre] deselected; txt->metrics:",this.label.getTextMetrics());
                if (this.deselectedTextStyle.metrics) {
                    // NOTE: myText.setStyle recalculates metrics [Samsung Internet fail]
                    // must set style without updating text, set metrics to text and update text manually.
                    this.label.style.setStyle(this.deselectedTextStyle, false, false);
                    this.label.style["metrics"] = this.deselectedTextStyle.metrics;
                    this.label.updateText();
                }
                else {
                    this.label.setStyle(this.deselectedTextStyle);
                }
                // console.log("[post] deselected; txt->metrics:",this.label.getTextMetrics());
            }
            this.label.text = this.deselectedLabel;
        }
    }
    
    /**
     * TODO: youll prob want to have 'disabled' icon state at least (maybe even 'selected'),
     *       so then youll need this override to apply it (and if you got 'selected' then also 'UpdateViewFromSelected')
     */
    protected override UpdateViewFromInteractive():void
    {
        // toggle button texture view update
        super.UpdateViewFromInteractive();

        // was disabled, is enabled
        if (this.isInteractive) {
            // is not selected (because if it is selected, we already using 'selected' texture with priority)
            if (!this.isSelected) {
                // icon
                // was using disabled texture
                if (this.iconTextureDatas.disabled != null) {
                    // set 'out' texture
                    this.icon.setTexture(
                        this.iconTextureDatas.out.texture, 
                        this.iconTextureDatas.out.frame);
                }
            }
        }
        // was enabled, is disabled
        else {
            // is not selected
            if (!this.isSelected) {
                // if got disabled texture
                if (this.iconTextureDatas.disabled != null) {
                    // set disabled texture
                    this.icon.setTexture(this.iconTextureDatas.disabled.texture, this.iconTextureDatas.disabled.frame);
                }
            }
        }
    }
    
    protected override OnPointerOver()
    {
        // console.log("ToggleButton::OnPointerOver");

        super.OnPointerOver();

        if (!this.isSelected) {
            if (this.isInteractive) {
                this.icon.setTexture(this.iconTextureDatas.over.texture, this.iconTextureDatas.over.frame);
                if (this.selectedTextStyle) {
                    if (this.selectedTextStyle.metrics) {
                        // NOTE: myText.setStyle recalculates metrics [Samsung Internet fail]
                        // must set style without updating text, set metrics to text and update text manually.
                        this.label.style.setStyle(this.selectedTextStyle, false, false);
                        this.label.style["metrics"] = this.selectedTextStyle.metrics;
                        this.label.updateText();
                    }
                    else {
                        this.label.setStyle(this.selectedTextStyle);
                    }
                }
            }
        }
    }
    
    protected override OnPointerOut()
    {
        // console.log("ToggleButton::OnPointerOut");

        super.OnPointerOut();

        if (!this.isSelected) {
            if (this.isInteractive) {
                this.icon.setTexture(this.iconTextureDatas.out.texture, this.iconTextureDatas.out.frame);
                if (this.deselectedTextStyle) {
                    if (this.deselectedTextStyle.metrics) {
                        // NOTE: myText.setStyle recalculates metrics [Samsung Internet fail]
                        // must set style without updating text, set metrics to text and update text manually.
                        this.label.style.setStyle(this.deselectedTextStyle, false, false);
                        this.label.style["metrics"] = this.deselectedTextStyle.metrics;
                        this.label.updateText();
                    }
                    else {
                        this.label.setStyle(this.deselectedTextStyle);
                    }
                }
            }
        }
    }
    
    public override destroy(): void
    {
        AppDebug.log("LabelIconToggleButton("+this.name+")::destroy", AppDebugLogType.DESTROY);

        super.destroy();
        this.label.destroy();

        this.label = null;
        this.iconTextureDatas = null;
        this.selectedLabel = null;
        this.deselectedLabel = null;
        this.deselectedTextStyle = null;
        this.selectedTextStyle = null;
    }
}